<template>
<BackpackBundleUpsell
    v-if="shouldShowBackpackPromotion"
    :backpack-activated="backpackActivated"
    :toggle-disabled="toggleDisabled"
    :backpack-bundle="backpackBundle"
    :backpack-normal-price-numeric="backpackNormalPriceNumeric"
    :backpack-price-numeric="backpackPriceNumeric"
    v-on:toggle="toggleBackpackPromotion"
/>
<BigBangBundleUpsell
    v-else-if="shouldShowPromotion && !discountIsNegative"
    :promotion-activated="promotionActivated"
    :toggle-disabled="toggleDisabled"
    :has-pp11="hasPP11"
    :has-survey-scramble="hasSurveyScramble"
    :upsale-product="upsaleProduct"
    :current-product="currentProduct"
    :big-bang-bundle="bigBangBundle"
    :discounted-price-numeric="discountedPriceNumeric"
    v-on:toggle="togglePromotion"
/>
<QuintpackUpsell
    v-else-if="shouldShowQuintPackUpsell && !quintPackDiscountIsNegative"
    :quint-pack-activated="quintPackActivated"
    :toggle-disabled="toggleDisabled"
    :quint-pack-tier="quintPackTier"
    :active-tier-party-pack-numbers="activeTierPartyPackNumbers"
    :active-tier-party-pack-configs="activeTierPartyPackConfigs"
    :party-pack-products="partyPackProducts"
    :active-quint-pack-product="activeQuintPackProduct"
    :quint-pack-normal-price-numeric="quintPackNormalPriceNumeric"
    :quint-pack-bundled-price-numeric="quintPackBundledPriceNumeric"
    v-on:toggle="toggleQuintPackPromotion"
/>
<PP12Upsell
    v-else-if="shouldShowPP12Upsale"
    :pp12="pp12"
    :loading="addPP12Loading"
    v-on:add="addPP12"
/>
<div v-else>
    <hr>
</div>
</template>

<script setup lang="ts">
import { type ComputedRef, type Ref, toRaw } from 'vue'
import Shopify from '@jackboxgames/shopify'
import { type JBGShopify, ShoppingCart } from '$services/shopify'
import { PromotionalProducts, getPartyPack } from '$services/upsell'
import BigBangBundleUpsell from './BigBangBundleUpsell.vue'
import QuintpackUpsell from './QuintpackUpsell.vue'
import PP12Upsell from './PP12Upsell.vue'
import BackpackBundleUpsell from './BackpackBundleUpsell.vue'

const app = useNuxtApp()

type UpsellTier = 'backpackBundle' | 'bigBangBundle' | 'quintPack1' | 'quintPack2'

const items = computed(() => toRaw(ShoppingCart.getCart()))

const quintPackUpsellFlagEnabled: Ref<boolean> = ref(false)

const activePromotionTier: Ref<UpsellTier | null> = ref(null)
type PromotionalItem = { variantId: string, qty: number }
const previousPromotionalItems: Ref<PromotionalItem[]> = ref([])

const toggleDisabled: Ref<boolean> = ref(false)
const addPP12Loading: Ref<boolean> = ref(false)

const surveyScramble: Ref<Shopify.Shopify.Product | null> = ref(null)
const pp11: Ref<Shopify.Shopify.Product | null> = ref(null)
const pp12: Ref<Shopify.Shopify.Product | null> = ref(null)
const bigBangBundle: Ref<Shopify.Shopify.Product | null> = ref(null)
const quintPack1: Ref<Shopify.Shopify.Product | null> = ref(null)
const quintPack2: Ref<Shopify.Shopify.Product | null> = ref(null)
const partyPackProducts: Ref<Record<string, Shopify.Shopify.Product | null>> = ref({})
const backpackBundle: Ref<Shopify.Shopify.Product | null> = ref(null)
const tmp3: Ref<Shopify.Shopify.Product | null> = ref(null)
const fakinItXL: Ref<Shopify.Shopify.Product | null> = ref(null)

const hasProduct = ((variantId: string): boolean => {
    let hasProduct = false
    items.value.forEach((item) => {
        if (item.variantId === variantId) {
            hasProduct = true
        }
    })
    return hasProduct
})

const getQty = ((variantId: string): number => {
    let qty = 0
    items.value.forEach((item) => {
        if (item.variantId === variantId) {
            qty = item.quantity
        }
    })
    return qty
})

const getAllPresentVariantIds = ((variantIds: string[]): string[] => variantIds.filter((variantId) => hasProduct(variantId)))

const quintPack1SourceVariantIds = [
    PromotionalProducts.PartyPack1.variantId,
    PromotionalProducts.PartyPack2.variantId,
    PromotionalProducts.PartyPack3.variantId,
    PromotionalProducts.PartyPack4.variantId,
    PromotionalProducts.PartyPack5.variantId
]
const quintPack2SourceVariantIds = [
    PromotionalProducts.PartyPack6.variantId,
    PromotionalProducts.PartyPack7.variantId,
    PromotionalProducts.PartyPack8.variantId,
    PromotionalProducts.PartyPack9.variantId,
    PromotionalProducts.PartyPack10.variantId
]

type PartyPackConfig = typeof PromotionalProducts.PartyPack1
const quintPack1PartyPackConfigs: PartyPackConfig[] = [
    PromotionalProducts.PartyPack1,
    PromotionalProducts.PartyPack2,
    PromotionalProducts.PartyPack3,
    PromotionalProducts.PartyPack4,
    PromotionalProducts.PartyPack5
]
const quintPack2PartyPackConfigs: PartyPackConfig[] = [
    PromotionalProducts.PartyPack6,
    PromotionalProducts.PartyPack7,
    PromotionalProducts.PartyPack8,
    PromotionalProducts.PartyPack9,
    PromotionalProducts.PartyPack10
]
const allPartyPackConfigs: PartyPackConfig[] = [...quintPack1PartyPackConfigs, ...quintPack2PartyPackConfigs]

const backpackSourceVariantIds = [
    PromotionalProducts.TMP3.variantId,
    PromotionalProducts.PartyPack12.variantId,
    PromotionalProducts.FakinItXL.variantId
]

const hasSurveyScramble: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.SurveyScramble.variantId))
const hasPP11: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.PartyPack11.variantId))
const hasPP12: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.PartyPack12.variantId))
const hasBigBangBundle: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.BigBangBundle.variantId))
const hasTMP3: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.TMP3.variantId))
const hasFakinItXL: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.FakinItXL.variantId))
const hasBackpackBundle: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.BackpackBundle.variantId))

const partyPackNumbersByTier: Record<'quintPack1' | 'quintPack2', number[]> = {
    quintPack1: [1, 2, 3, 4, 5],
    quintPack2: [6, 7, 8, 9, 10]
}

const hasQuintPack1: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.QuintPack1.variantId))
const hasQuintPack2: ComputedRef<boolean> = computed(() => hasProduct(PromotionalProducts.QuintPack2.variantId))

const promotionActivated: ComputedRef<boolean> = computed(() => activePromotionTier.value === 'bigBangBundle')
const quintPackActivated: ComputedRef<boolean> = computed(() => (
    activePromotionTier.value === 'quintPack1' || activePromotionTier.value === 'quintPack2'
))
const backpackActivated: ComputedRef<boolean> = computed(() => (
    activePromotionTier.value === 'backpackBundle'
))

// eslint-disable-next-line max-len
const shouldShowPromotion: ComputedRef<boolean> = computed(() => (activePromotionTier.value === 'bigBangBundle') || (hasSurveyScramble.value && !hasPP11.value && !hasBigBangBundle.value) || (hasPP11.value && !hasSurveyScramble.value && !hasBigBangBundle.value))
// eslint-disable-next-line max-len
const shouldShowPP12Upsale: ComputedRef<boolean> = computed(() => !hasPP11.value && !hasPP12.value && !hasSurveyScramble.value && !hasBigBangBundle.value)

const shouldShowBackpackPromotion: ComputedRef<boolean> = computed(() => (
    activePromotionTier.value === 'backpackBundle'
    || (
        !hasBackpackBundle.value
        && (hasTMP3.value || hasPP12.value || hasFakinItXL.value)
    )
))

const isEligibleForQuintPack1: ComputedRef<boolean> = computed(() => (
    !hasQuintPack1.value && partyPackNumbersByTier.quintPack1.some((packNum) => hasProduct(getPartyPack(packNum)))
))
const isEligibleForQuintPack2: ComputedRef<boolean> = computed(() => (
    !hasQuintPack2.value && partyPackNumbersByTier.quintPack2.some((packNum) => hasProduct(getPartyPack(packNum)))
))

const activeUpsellTier: ComputedRef<UpsellTier | null> = computed(() => {
    if (activePromotionTier.value) { return activePromotionTier.value }
    if (shouldShowBackpackPromotion.value) { return 'backpackBundle' }
    if (shouldShowPromotion.value) { return 'bigBangBundle' }
    if (isEligibleForQuintPack2.value) { return 'quintPack2' }
    if (isEligibleForQuintPack1.value) { return 'quintPack1' }
    return null
})

const shouldShowQuintPackUpsell: ComputedRef<boolean> = computed(() => (
    quintPackUpsellFlagEnabled.value
    && (activeUpsellTier.value === 'quintPack1' || activeUpsellTier.value === 'quintPack2')
))

const quintPackTier: ComputedRef<'quintPack1' | 'quintPack2' | null> = computed(() => (
    (activeUpsellTier.value === 'quintPack1' || activeUpsellTier.value === 'quintPack2') ? activeUpsellTier.value : null
))

type QuintPackConfig = typeof PromotionalProducts.QuintPack1 | typeof PromotionalProducts.QuintPack2
const quintPackUpsellProduct: ComputedRef<QuintPackConfig | null> = computed(() => {
    if (activeUpsellTier.value === 'quintPack1') { return PromotionalProducts.QuintPack1 }
    if (activeUpsellTier.value === 'quintPack2') { return PromotionalProducts.QuintPack2 }
    return null
})

const activeQuintPackProduct: ComputedRef<Shopify.Shopify.Product | null> = computed(() => {
    if (activeUpsellTier.value === 'quintPack1') { return quintPack1.value }
    if (activeUpsellTier.value === 'quintPack2') { return quintPack2.value }
    return null
})

const activeTierPartyPackNumbers: ComputedRef<number[]> = computed(() => {
    if (activeUpsellTier.value === 'quintPack1') { return partyPackNumbersByTier.quintPack1 }
    if (activeUpsellTier.value === 'quintPack2') { return partyPackNumbersByTier.quintPack2 }
    return []
})

const activeTierPartyPackConfigs: ComputedRef<PartyPackConfig[]> = computed(() => {
    if (activeUpsellTier.value === 'quintPack1') { return quintPack1PartyPackConfigs }
    if (activeUpsellTier.value === 'quintPack2') { return quintPack2PartyPackConfigs }
    return []
})

const quintPackNormalPriceNumeric: ComputedRef<number> = computed(() => activeTierPartyPackConfigs.value.reduce((sum, config) => {
    const product = partyPackProducts.value[config.variantId]
    if (!product) { return sum }
    return sum + +product.priceRange.maxVariantPrice.amount
}, 0))

const quintPackBundledPriceNumeric: ComputedRef<number> = computed(() => {
    if (!activeQuintPackProduct.value) { return 0 }
    return +activeQuintPackProduct.value.priceRange.maxVariantPrice.amount
})

const quintPackDiscountIsNegative: ComputedRef<boolean> = computed(() => {
    if (quintPackActivated.value) { return false }
    if (quintPackNormalPriceNumeric.value <= 0) { return true }
    return quintPackBundledPriceNumeric.value >= quintPackNormalPriceNumeric.value
})

const backpackNormalPriceNumeric: ComputedRef<number> = computed(() => [pp12.value, tmp3.value, fakinItXL.value].reduce((sum, product) => {
    if (!product) { return sum }
    return sum + +product.priceRange.maxVariantPrice.amount
}, 0))

const backpackPriceNumeric: ComputedRef<number> = computed(() => {
    if (!backpackBundle.value) { return 0 }
    return +backpackBundle.value.priceRange.maxVariantPrice.amount
})

const currentProduct: ComputedRef<Shopify.Shopify.Product | null> = computed(() => {
    if (!shouldShowPromotion.value) { return null }
    if (hasPP11.value) { return pp11.value }
    if (hasSurveyScramble.value) { return surveyScramble.value }
    return null
})

const upsaleProduct: ComputedRef<Shopify.Shopify.Product | null> = computed(() => {
    if (!shouldShowPromotion.value) { return null }
    if (hasPP11.value) { return surveyScramble.value }
    if (hasSurveyScramble.value) { return pp11.value }
    return null
})

const discountedPriceNumeric: ComputedRef<number> = computed(() => {
    if (!currentProduct.value || !bigBangBundle.value) {
        return 0
    }

    const bigBangBundlePrice = bigBangBundle.value.priceRange.maxVariantPrice.amount
    const currentProductPrice = currentProduct.value.priceRange.maxVariantPrice.amount

    return +bigBangBundlePrice - +currentProductPrice || 0
})

const discountIsNegative: ComputedRef<boolean> = computed(() => {
    if (promotionActivated.value) {
        return false
    }
    if (discountedPriceNumeric.value <= 0) {
        return true
    }
    return (discountedPriceNumeric.value >= +upsaleProduct.value.priceRange.minVariantPrice.amount)
})

type ProductFulfilledHandler = (product: Shopify.Shopify.Product) => void
type ProductSettledResult = PromiseSettledResult<Shopify.Shopify.Product>
const applyFetchResult = ((label: string, result: ProductSettledResult, onFulfilled: ProductFulfilledHandler) => {
    if (result.status === 'fulfilled') {
        onFulfilled(result.value)
    } else {
        console.error(`BigBangBundleUpsell: failed to fetch ${label}`, result.reason)
    }
})

onMounted(async () => {
    quintPackUpsellFlagEnabled.value = app.$launchDarkly.useFlag('upselling-in-the-cart-quintpacks', false).value

    const [
        surveyScrambleResult,
        pp11Result,
        pp12Result,
        bigBangBundleResult,
        quintPack1Result,
        quintPack2Result,
        backpackBundleResult,
        tmp3Result,
        fakinItXLResult,
        ...partyPackResults
    ] = await Promise.allSettled([
        app.$shopify.fetchProduct(PromotionalProducts.SurveyScramble.slug),
        app.$shopify.fetchProduct(PromotionalProducts.PartyPack11.slug),
        app.$shopify.fetchProduct(PromotionalProducts.PartyPack12.slug),
        app.$shopify.fetchProduct(PromotionalProducts.BigBangBundle.slug),
        app.$shopify.fetchProduct(PromotionalProducts.QuintPack1.slug),
        app.$shopify.fetchProduct(PromotionalProducts.QuintPack2.slug),
        app.$shopify.fetchProduct(PromotionalProducts.BackpackBundle.slug),
        app.$shopify.fetchProduct(PromotionalProducts.TMP3.slug),
        app.$shopify.fetchProduct(PromotionalProducts.FakinItXL.slug),
        ...allPartyPackConfigs.map((config) => app.$shopify.fetchProduct(config.slug))
    ])

    applyFetchResult('SurveyScramble', surveyScrambleResult, (product) => { surveyScramble.value = product })
    applyFetchResult('PartyPack11', pp11Result, (product) => { pp11.value = product })
    applyFetchResult('PartyPack12', pp12Result, (product) => { pp12.value = product })
    applyFetchResult('BigBangBundle', bigBangBundleResult, (product) => { bigBangBundle.value = product })
    applyFetchResult('QuintPack1', quintPack1Result, (product) => { quintPack1.value = product })
    applyFetchResult('QuintPack2', quintPack2Result, (product) => { quintPack2.value = product })
    applyFetchResult('BackpackBundle', backpackBundleResult, (product) => { backpackBundle.value = product })
    applyFetchResult('TMP3', tmp3Result, (product) => { tmp3.value = product })
    applyFetchResult('FakinItXL', fakinItXLResult, (product) => { fakinItXL.value = product })
    allPartyPackConfigs.forEach((config, index) => {
        applyFetchResult(config.slug, partyPackResults[index], (product) => {
            partyPackProducts.value[config.variantId] = product
        })
    })
})

const activateTier = (async (tier: UpsellTier, bundleVariantId: string, sourceVariantIds: string[]) => {
    toggleDisabled.value = true
    try {
        const sourceItems: PromotionalItem[] = sourceVariantIds.map((variantId) => ({ variantId, qty: getQty(variantId) }))
        if (sourceItems.length === 1) {
            await app.$shopify.swapItemsByVariantId(sourceItems[0].variantId, bundleVariantId, sourceItems[0].qty)
        } else {
            await app.$shopify.swapManyForOneByVariantId(sourceVariantIds, bundleVariantId, 1)
        }
        activePromotionTier.value = tier
        previousPromotionalItems.value = sourceItems
    } catch (e) {
        console.error(e)
    } finally {
        toggleDisabled.value = false
    }
})

const deactivateTier = (async (bundleVariantId: string) => {
    toggleDisabled.value = true
    try {
        const restoreItems = previousPromotionalItems.value
        if (restoreItems.length === 1) {
            await app.$shopify.swapItemsByVariantId(bundleVariantId, restoreItems[0].variantId, restoreItems[0].qty)
        } else {
            await app.$shopify.swapOneForManyByVariantId(bundleVariantId, restoreItems)
        }
        activePromotionTier.value = null
        previousPromotionalItems.value = []
    } catch (e) {
        console.error(e)
    } finally {
        toggleDisabled.value = false
    }
})

const togglePromotion = (async () => {
    if (!shouldShowPromotion.value) { return }
    if (activePromotionTier.value !== 'bigBangBundle') {
        const sourceVariantId = hasSurveyScramble.value
            ? PromotionalProducts.SurveyScramble.variantId
            : PromotionalProducts.PartyPack11.variantId
        await activateTier('bigBangBundle', PromotionalProducts.BigBangBundle.variantId, [sourceVariantId])
    } else {
        await deactivateTier(PromotionalProducts.BigBangBundle.variantId)
    }
})

const toggleQuintPackPromotion = (async () => {
    const tier = activeUpsellTier.value
    if ((tier !== 'quintPack1' && tier !== 'quintPack2') || !quintPackUpsellProduct.value) { return }

    const bundleVariantId = quintPackUpsellProduct.value.variantId

    if (activePromotionTier.value !== tier) {
        const sourceVariantIds = tier === 'quintPack1' ? quintPack1SourceVariantIds : quintPack2SourceVariantIds
        const presentVariantIds = getAllPresentVariantIds(sourceVariantIds)
        if (!presentVariantIds.length) { return }
        await activateTier(tier, bundleVariantId, presentVariantIds)
    } else {
        await deactivateTier(bundleVariantId)
    }
})

const toggleBackpackPromotion = async () => {
    if (!shouldShowBackpackPromotion.value) {
        return
    }

    if (activePromotionTier.value !== 'backpackBundle') {
        const presentVariantIds = getAllPresentVariantIds(backpackSourceVariantIds)

        if (!presentVariantIds.length) {
            return
        }

        await activateTier('backpackBundle', PromotionalProducts.BackpackBundle.variantId, presentVariantIds)
    } else {
        await deactivateTier(PromotionalProducts.BackpackBundle.variantId)
    }
}

const addPP12 = (async (event: Event) => {
    event.stopPropagation()
    if (!addPP12Loading.value) {
        addPP12Loading.value = true
        try {
            await app.$shopify.addItemToCartByVariantId(PromotionalProducts.PartyPack12.variantId, 1, pp12.value).then(() => {
                addPP12Loading.value = false
            })
        } catch (e) {
            console.error(e)
            addPP12Loading.value = false
        }
    }
})

const activeTierBundleVariantId: ComputedRef<string | null> = computed(() => {
    if (activePromotionTier.value === 'backpackBundle') { return PromotionalProducts.BackpackBundle.variantId }
    if (activePromotionTier.value === 'bigBangBundle') { return PromotionalProducts.BigBangBundle.variantId }
    if (activePromotionTier.value === 'quintPack1') { return PromotionalProducts.QuintPack1.variantId }
    if (activePromotionTier.value === 'quintPack2') { return PromotionalProducts.QuintPack2.variantId }
    return null
})

const didRemoveItem = ((item: JBGShopify.DisplayItem) => {
    if (activeTierBundleVariantId.value && item.variantId === activeTierBundleVariantId.value) {
        previousPromotionalItems.value = []
        activePromotionTier.value = null
    }
})

defineExpose({ didRemoveItem })

</script>

<style scoped lang="scss">
hr {
    border: none;
    height: 1px;
    color: var(--neutral-300);
    background-color: var(--neutral-300);
    margin-top: 26px;
    @media only screen and (max-width: 599px) {
        margin-top: 18px;
    }
}
</style>
